import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../services/api.service';
import { Company, SymbolMatch, UserProfile } from '../../models/profile.model';

@Component({
  selector: 'app-profile-form',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './profile-form.component.html',
})
export class ProfileFormComponent implements OnInit {
  profile: UserProfile = {
    personal_details: { full_name: '', email: '', phone: '', country: '', bio: '' },
    preferred_companies: [{ name: '', symbol: '' }],
  };

  saving = false;
  errorMessage = '';
  successMessage = '';

  lookupOpen = false;
  lookupCompanyIndex: number | null = null;
  lookupQuery = '';
  lookupLoading = false;
  lookupSearched = false;
  lookupResults: SymbolMatch[] = [];

  notificationMessage = '';
  notificationVisible = false;
  private notificationTimeoutId: ReturnType<typeof setTimeout> | null = null;

  constructor(private api: ApiService, private router: Router) {}

  ngOnInit(): void {
    this.api.getProfile().subscribe({
      next: (p) => {
        this.profile = p;
        if (!this.profile.preferred_companies.length) {
          this.addCompany();
        }
      },
      error: () => {
        // No existing profile yet -- that's fine, keep the blank form.
      },
    });
  }

  addCompany(): void {
    this.profile.preferred_companies.push({ name: '', symbol: '' });
  }

  removeCompany(index: number): void {
    this.profile.preferred_companies.splice(index, 1);
  }

  openSymbolLookup(index: number): void {
    this.lookupCompanyIndex = index;
    this.lookupQuery = this.profile.preferred_companies[index]?.name || '';
    this.lookupResults = [];
    this.lookupSearched = false;
    this.lookupOpen = true;
  }

  closeSymbolLookup(): void {
    this.lookupOpen = false;
    this.lookupCompanyIndex = null;
  }

  runSymbolLookup(): void {
    const query = this.lookupQuery.trim();
    if (!query) {
      return;
    }

    this.lookupLoading = true;
    this.lookupSearched = false;
    this.api.searchSymbol(query).subscribe({
      next: (res) => {
        this.lookupLoading = false;
        this.lookupSearched = true;
        this.lookupResults = res.matches || [];
        if (!this.lookupResults.length) {
          this.showNotification(`No ticker symbol found for "${query}".`);
        }
      },
      error: () => {
        this.lookupLoading = false;
        this.lookupSearched = true;
        this.lookupResults = [];
        this.showNotification(`No ticker symbol found for "${query}".`);
      },
    });
  }

  selectSymbol(match: SymbolMatch): void {
    if (this.lookupCompanyIndex === null) {
      return;
    }
    this.profile.preferred_companies[this.lookupCompanyIndex].symbol = match.symbol;
    this.showNotification(`Symbol "${match.symbol}" applied.`);
    this.closeSymbolLookup();
  }

  private showNotification(message: string): void {
    this.notificationMessage = message;
    this.notificationVisible = true;
    if (this.notificationTimeoutId) {
      clearTimeout(this.notificationTimeoutId);
    }
    this.notificationTimeoutId = setTimeout(() => {
      this.notificationVisible = false;
    }, 4000);
  }

  dismissNotification(): void {
    this.notificationVisible = false;
    if (this.notificationTimeoutId) {
      clearTimeout(this.notificationTimeoutId);
    }
  }

  submit(): void {
    this.errorMessage = '';
    this.successMessage = '';

    const cleanCompanies: Company[] = this.profile.preferred_companies
      .filter((c) => c.name && c.name.trim().length > 0)
      .map((c) => ({ name: c.name.trim(), symbol: c.symbol?.trim() || null }));

    if (!cleanCompanies.length) {
      this.errorMessage = 'Please add at least one preferred company.';
      return;
    }

    this.saving = true;
    const payload: UserProfile = {
      personal_details: this.profile.personal_details,
      preferred_companies: cleanCompanies,
    };

    this.api.saveProfile(payload).subscribe({
      next: () => {
        this.saving = false;
        this.successMessage = 'Profile saved!';
        this.router.navigate(['/dashboard']);
      },
      error: (err) => {
        this.saving = false;
        this.errorMessage = err?.error?.detail || 'Failed to save profile.';
      },
    });
  }
}
